import "./input-box-view.less";
import template from "./input-box-view.html";

import { OptionalCSSStyles } from "@educationperfect/ep-web-browser-utils";
import { NumberBoxComponentMetaData } from "@educationperfect/ep-web-services/lib/services/Questions/BusinessObjects/Questions/NumberBoxComponentMetaData";
import { TextBoxComponentMetaData } from "@educationperfect/ep-web-services/lib/services/Questions/BusinessObjects/Questions/TextBoxComponentMetaData";
import { Editor, NodeView } from "@educationperfect/tiptap";
import { Node as ProsemirrorNode } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import { Component, Prop, Vue } from "vue-property-decorator";

import { EditorPluginState } from "../../../../../components/editor/interfaces/EditorPluginState";
import { EditorPlugin } from "../../../../../components/editor/plugins/EditorPlugin";
import { InlineComponentFloatingToolbar } from "../../../../../components/inlineComponentFloatingToolbar/InlineComponentFloatingToolbar";
import { TipTapComponents } from "../../../../../TipTapComponents";
import { ProsemirrorUtils } from "../../../../../utils/ProsemirrorUtils";
import { EditorEvents } from "../../../../editorEvents/EditorEvents";
import { EditorEventsPlugin, EditorEventsState } from "../../../../editorEvents/EditorEventsPlugin";
import { NumberBoxEditor } from "../../numberBox/editor/NumberBoxEditor";
import { TextBoxEditor } from "../../textBox/editor/TextBoxEditor";

/** The default widht of the number box component */
const DEFAULT_NUMBER_BOX_WIDTH: number = 200;

/** The two types of input box */
type ComponentMetaData = NumberBoxComponentMetaData | TextBoxComponentMetaData;

@Component({
    template,
    components: {
        [TipTapComponents.NumberBoxEditor]: NumberBoxEditor,
        [TipTapComponents.TextBoxEditor]: TextBoxEditor,
        [TipTapComponents.InlineComponentFloatingToolbar]: InlineComponentFloatingToolbar,
    },
})
export class InputBoxView extends Vue implements NodeView {
    // Variables
    // ======================================

    // TipTap Props

    /** The current node */
    @Prop() public node!: ProsemirrorNode;

    /** The instance of the editor */
    @Prop() public editor!: Editor;

    /** Whether or not the node is selected */
    @Prop() public selected!: boolean;

    /** The current editor view */
    @Prop() public view!: EditorView;

    // State

    /** The component metadata */
    private componentMetaData: ComponentMetaData | null = null;

    /** Whether or not the editor audio dialig is open */
    private editorOpen: boolean = false;

    /** Keeps track of whether the editor has focus - used for displaying/hiding the inline toolbar */
    private hasFocus: boolean = false;

    // Lifecycle Events
    // ======================================

    /** Lifecyle Hook: Called when the component is first mounted to the DOM. Get the components metadata */
    private mounted(): void {
        this.componentMetaData = this.getComponentMetaData();
        this.editor.on("focus", () => {
            this.hasFocus = true;
        });
        this.editor.on("blur", ({ event }: { event: FocusEvent }) => {
            this.hasFocus = ProsemirrorUtils.handleToolbarClickBlurEvent(event);
        });
    }

    private destroyed(): void {
        // this.editor.off("focus", () => {});
        // this.editor.off("blur", () => {});
    }

    // Logic
    // ======================================

    /**
     * Get the components metadata via plugin callback
     */
    private getComponentMetaData(): ComponentMetaData | null {
        const pluginState: EditorPluginState = EditorPlugin.EditorPluginKey.getState(this.editor.state);
        if (!pluginState || !pluginState.getComponentById) {
            return null;
        }

        return pluginState.getComponentById(this.id) as ComponentMetaData;
    }

    // DOM Events
    // ======================================

    /**
     * DOM Emit:
     * Called when the number box editor closes.
     * Dispatch update to the component meta data
     *
     * @param componentMetaData the metadata from the editor
     */
    private onEditorSave(componentMetaData: ComponentMetaData): void {
        const pluginState: EditorEventsState = EditorEventsPlugin.EditorEventsPluginKey.getState(this.editor.state);
        if (!pluginState) {
            return;
        }

        pluginState.dispatch(EditorEvents.ComponentMetaDataChange, {
            componentId: this.id,
            metaData: componentMetaData,
        });
        this.componentMetaData = this.getComponentMetaData();
    }

    // Gets
    // ======================================

    /** Get the id of the component */
    private get id(): string {
        return this.node.attrs.id;
    }

    /** The the styles for the input field */
    private get inputStyles(): OptionalCSSStyles {
        const styles: OptionalCSSStyles = {
            width: `${DEFAULT_NUMBER_BOX_WIDTH}px`,
        };

        if (!this.componentMetaData) {
            return styles;
        }

        if (this.componentMetaData.Width) {
            styles.width = `${this.componentMetaData.Width}px`;
        }

        if (this.componentMetaData.TextAlignment) {
            styles.textAlign = this.componentMetaData.TextAlignment;
        }

        return styles;
    }

    /**
     * Get the container class for the input box. This is using a class
     * instead of inline style as otherwise the dropdown gets wrapped in sub
     * and allows a broken state due to it being a mark.
     */
    private get containerClass(): string {
        return this.componentMetaData?.ScriptPosition ?? "";
    }

    /** Get the plugin editor state */
    public get editorPluginState(): EditorPluginState | null {
        const pluginState: EditorPluginState = EditorPlugin.EditorPluginKey.getState(this.editor.state);
        if (!pluginState) {
            return null;
        }

        return pluginState;
    }

    /** Get the base language id of the current editing lesson */
    private get baseLanguageId(): number | undefined {
        return this.editorPluginState?.baseLanguageId?.();
    }

    /** Get the target language id of the current editing lesson */
    private get targetLanguageId(): number | undefined {
        return this.editorPluginState?.targetLanguageId?.();
    }

    /**
     * Check that the inline node is selected and the editor it lives in is also focused.
     * This is done because we want to keep the selected state when the user clicks away
     * from a given editor instance (ie: clicking from one text block to another), but
     * we don't want the selected state border to persist.
     */
    private get isSelected(): boolean {
        return this.selected && this.hasFocus;
    }
}
